<template>
  <div>
    <div class="ui form">
      <div class="inline fields">
        <!-- CHAT SERVICE TYPE -->
        <label>{{ $tc('CHAT_SERVICE_TYPE', 1) }}</label>
        <div class="field" v-for="actionType in actionTypes" :key="actionType.id">
          <div class="ui radio checkbox">
            <input
              type="radio"
              name="actionType"
              :id="'actionnType-' + actionType.id"
              :value="actionType.id"
              v-model="actionTypeId"
            />
            <label :for="'actionType' + actionType.id">
              {{ actionType.name }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <div v-if="a && a.type == 'MAIL' && a.mail">
      <div class="ui form">
        <!-- EMAIL SERVICE SELECTOR -->
        <div class="field required" :class="{ error: $v.a.service.$error }">
          <typeahead
            :max-items="100"
            :current-item="nameOfService(a.service)"
            :items="allMailServices"
            :label-text="$t('CHOOSE', { thing: $tc('MAIL_SERVICE', 1) }) + '...'"
            :placeholder-text="$tc('MAIL_SERVICE', 1)"
            :empty-text="$t('NO_ITEMS_MATCH', { things: $tc('MAIL_SERVICE', 2) })"
            :select-handler="setService"
            :remove-handler="removeService"
          >
            <template slot="dropdown" slot-scope="props">
              {{ $t(props.item.name) }}
            </template>
          </typeahead>
        </div>

        <div class="two fields">
          <!-- TO -->
          <div class="field" :class="{ error: $v.a.mail.to.$error }">
            <label>{{ $tc('TO', 1) }}</label>
            <input
              type="text"
              name="to"
              v-model="a.mail.to"
              :placeholder="$tc('TO', 1)"
              @click="$v.a.mail.to.$touch()"
            />
          </div>
          <!-- SUBJECT -->
          <div class="field">
            <label>{{ $tc('EMAIL_SUBJECT', 1) }}</label>
            <input
              type="text"
              name="subject"
              v-model="a.mail.subject"
              :placeholder="$tc('EMAIL_SUBJECT', 1)"
            />
          </div>
        </div>
      </div>

      <!-- EMAIL BODY -->
      <div class="field">
        <TemplateEditor
          :labelText="$tc('EMAIL_BODY', 1)"
          :value="a.mail.body"
          height="100px"
          @contentchanged="mailBodyChanged"
          :variables="[{ name: 'message', description: $t('ALERT_MESSAGE_INTERPOLATION') }]"
        />
      </div>
    </div>

    <!-- CHAT -->
    <div v-if="a && a.type == 'CHAT'">
      <form class="ui form">
        <!-- CHAT SERVICE SELECTOR -->
        <div class="field required" :class="{ error: $v.a.service.$error }">
          <typeahead
            :max-items="100"
            :current-item="nameOfService(a.service)"
            :items="allChatServices"
            :label-text="$t('CHOOSE', { thing: $tc('CHAT_SERVICE', 1) }) + '...'"
            :placeholder-text="$tc('CHAT_SERVICE', 1)"
            :empty-text="$t('NO_ITEMS_MATCH', { things: $tc('CHAT_SERVICE', 2) })"
            :select-handler="setService"
            :remove-handler="removeService"
          >
            <template slot="dropdown" slot-scope="props">
              {{ $t(props.item.name) }}
            </template>
          </typeahead>
        </div>

        <!-- CHANNEL -->
        <div class="field">
          <label>{{ $t('CHAT_CHANNEL') }}</label>
          <input
            type="text"
            name="channel"
            v-model="a.chat.channel"
            :placeholder="$t('CHAT_CHANNEL')"
          />
        </div>
      </form>

      <!-- CHAT MESSAGE -->
      <div class="field">
        <TemplateEditor
          :labelText="$t('CHAT_MESSAGE')"
          :value="a.chat.text"
          height="100px"
          @contentchanged="chatTextChanged"
          :variables="[{ name: 'message', description: $t('ALERT_MESSAGE_INTERPOLATION') }]"
        />
      </div>
    </div>
    <div class="ui mini red labeled icon button" @click="onDelete">
      <i class="delete icon"></i>
      {{ $t('DELETE', { thing: $tc('ALERT_RULE_ACTION', 1) }) }}
    </div>
  </div>
</template>

<script>
import { TemplateEditor, Typeahead } from 'ui'
import { Service } from 'services'
import { email, required } from 'vuelidate/lib/validators'

export default {
  name: 'AlertAction',

  props: ['action'],

  components: { TemplateEditor, Typeahead },

  validations: {
    a: {
      service: {
        required
      },
      mail: {
        to: {
          email
        }
      }
    }
  },

  computed: {
    allServices() {
      return this.$store.state.service.items
    },
    allMailServices() {
      // We can only do IMAP/SMTP alerts for now
      return _.map(
        _.filter(
          this.allServices,
          s => s.serviceType === 'MAIL' && s.serviceConfiguration.type === 'IMAP'
        ),
        s => {
          return { name: s.name || s.id, key: s.id, id: s.id, value: s.id }
        }
      )
    },
    allChatServices() {
      return _.map(_.filter(this.allServices, s => s.serviceType === 'CHAT'), s => {
        return { name: s.name || s.id, key: s.id, id: s.id, value: s.id }
      })
    }
  },

  methods: {
    nameOfService(serviceId) {
      return (
        _.find([...this.allChatServices, ...this.allMailServices], s => s.id === serviceId)?.name ??
        serviceId
      )
    },
    mailBodyChanged(content) {
      this.a.mail.body = content
    },

    chatTextChanged(content) {
      this.a.chat.text = content
    },
    removeService(item) {
      this.$set(this.a, 'service', null)
      this.$v.a.service.$touch()
    },

    setService(s) {
      this.$set(this.a, 'service', s.id)
      this.$emit('change', this.a)
      this.$v.a.service.$touch()
    },

    switchActionType(type) {
      if (type === 'MAIL') this.a = { id: this.a.id, type, mail: { to: '', subject: '', body: '' } }
      if (type === 'CHAT') this.a = { id: this.a.id, type, chat: { channel: '', text: '' } }

      this.$emit('change', this.a)
    },

    onDelete() {
      this.$emit('delete', this.a)
    },

    onAdd() {
      this.$emit('add')
    }
  },

  data() {
    return {
      a: _.cloneDeep(this.action),
      actionTypeId: this.action.type,
      actionTypes: [
        {
          name: this.$t('ALERT_ACTION_TYPE_MAIL'),
          id: 'MAIL'
        },
        {
          name: this.$t('ALERT_ACTION_TYPE_CHAT'),
          id: 'CHAT'
        }
      ]
    }
  },

  mounted() {
    Service.readAllServices()
  },

  watch: {
    'a.mail': {
      handler(newMail) {
        this.$emit('change', { ...this.a, mail: newMail })
      },
      deep: true
    },
    'a.chat': {
      handler(newChat) {
        this.$emit('change', { ...this.a, chat: newChat })
      },
      deep: true
    },
    actionTypeId: function(newActionType) {
      this.switchActionType(newActionType)
    }
  }
}
</script>
